import { spawn, type SpawnOptions } from 'node:child_process';
import { Data, Effect } from 'effect';

export class LauncherError extends Data.TaggedError('LauncherError')<{
  readonly reason: 'spawn' | 'exit';
  readonly exitCode?: number;
}> {}

const causes = new WeakMap<LauncherError, unknown>();

function failure(reason: LauncherError['reason'], cause: unknown, code?: number): LauncherError {
  const error = new LauncherError({ reason, ...(code === undefined ? {} : { exitCode: code }) });
  causes.set(error, cause);
  return error;
}

export const launcherErrorCause = (error: LauncherError): unknown => causes.get(error);

/** Own only the launcher, not the application it opens. Close joins its OS/stdio lifetime. */
export const launchProcessEffect = Effect.fn('okrapdf/cli.launchProcess')(function* (
  file: string,
  args: string[],
  options: SpawnOptions,
) {
  // runPromise must observe an already-aborted signal before spawning foreign work.
  yield* Effect.yieldNow;
  return yield* Effect.scoped(
    Effect.gen(function* () {
      const owner = yield* Effect.acquireRelease(
        Effect.try({
          try: () => {
            const child = spawn(file, args, options);
            let closed = false;
            let code: number | null = null;
            let error: unknown;
            const waiters = new Set<() => void>();
            const resultWaiters = new Set<() => void>();
            // Keep the handler through close: a failed kill or failed spawn can emit error
            // before the terminal close event. It must not become an unhandled exception.
            const onError = (cause: Error) => {
              error ??= cause;
              for (const resume of resultWaiters) resume();
              resultWaiters.clear();
            };
            const onClose = (exitCode: number | null) => {
              closed = true;
              code = exitCode;
              for (const resume of waiters) resume();
              waiters.clear();
              for (const resume of resultWaiters) resume();
              resultWaiters.clear();
            };
            child.on('error', onError);
            child.once('close', onClose);
            const waitClosed = Effect.callback<void>((resume) => {
              const done = () => resume(Effect.void);
              if (closed) done();
              else waiters.add(done);
              return Effect.sync(() => {
                waiters.delete(done);
              });
            });
            const waitResult = Effect.callback<void>((resume) => {
              const done = () => resume(Effect.void);
              if (closed || error !== undefined) done();
              else resultWaiters.add(done);
              return Effect.sync(() => {
                resultWaiters.delete(done);
              });
            });
            const terminate = (signal: NodeJS.Signals) =>
              Effect.sync(() => {
                if (closed || child.exitCode !== null || child.signalCode !== null || !child.pid)
                  return;
                try {
                  child.kill(signal);
                } catch (cause) {
                  error ??= cause;
                }
              });
            const close = Effect.gen(function* () {
              if (!closed) {
                yield* terminate('SIGTERM');
                // Give the owned launcher time to exit gracefully, then stop only that PID.
                yield* Effect.raceFirst(
                  waitClosed,
                  Effect.sleep(1000).pipe(
                    Effect.andThen(terminate('SIGKILL')),
                    Effect.andThen(waitClosed),
                  ),
                );
              }
            }).pipe(
              Effect.ensuring(
                Effect.sync(() => {
                  child.off('error', onError);
                  child.off('close', onClose);
                  waiters.clear();
                  resultWaiters.clear();
                }),
              ),
            );
            return { waitResult, close, outcome: () => ({ code, error }) };
          },
          catch: (cause) => failure('spawn', cause),
        }),
        (owner) => owner.close,
      );
      yield* owner.waitResult;
      const { code, error } = owner.outcome();
      if (error !== undefined) return yield* Effect.fail(failure('spawn', error));
      if (code !== 0) {
        return yield* Effect.fail(
          failure(
            'exit',
            new Error(`${file} exited with code ${code ?? 'unknown'}.`),
            code ?? undefined,
          ),
        );
      }
    }),
  );
});
